import React, { FC, useEffect } from 'react';

import { Box } from '@mui/system';

import {
  currency,
  getWorkshiftsByDate,
  logos,
  pharmacistActions,
  PharmacistLegendVariant,
  toWeekdayWeekendFormat,
  userPreferredTimeFormat,
} from '@pharmaplan/common';

import {
  renderScreen,
  setBack,
  setDrawerContentPadding,
  setFooterShow,
  setTitle,
} from '../../actions/drawerActions';

import getLongTermContractLabels from '../common/LongTermContractLabels/LongTermContractLabels';
import DrawerOutOfDateTitle from '../common/DrawerOutOfDateTitle';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { useAppSelector } from '../../hooks/useAppSelector';
import CustomAvatarSection from '../common/CustomAvatarSection';
import { ChipType, ScreenTypes } from '../../helpers/Constants';
import useMultiShiftFilter from '../../hooks/useMultiShiftFilter';
import CustomChip from '../common/CustomChip';

import WorkshiftListRightSection from './WorkshiftListRightSection';
import { pharmacistWorkshiftListBuilder } from './helper';
import strings from '../../localization';
import styles from './styles';

const { getWorkshiftsByDate: successAction } = pharmacistActions;
const { serviceWorkshiftDetails } = ScreenTypes;

interface IWorkshiftList {
  date: string;
}

interface IHandleClick {
  workshiftId: string;
  startDate: string;
  pharmacyId: string;
  longTermShiftContractId: string;
  isAutoSyncRecord: boolean;
}

const WorkshiftList: FC<IWorkshiftList> = ({ date }) => {
  const dispatch = useAppDispatch();

  const { pharmacistEvents: workshiftList } = useMultiShiftFilter();
  const allLogos = useAppSelector(logos);
  const timeFormat = useAppSelector(userPreferredTimeFormat);

  useEffect(() => {
    const title = (
      <DrawerOutOfDateTitle
        dateLabel={strings.availableWorkshiftFormatted}
        date={date}
      />
    );

    dispatch(setTitle({ title }));
    dispatch(setDrawerContentPadding('0px'));
    dispatch(setFooterShow(false));
    dispatch(setBack(null));
    dispatch(getWorkshiftsByDate(date?.split?.('T')?.[0]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const events = pharmacistWorkshiftListBuilder(
    workshiftList,
    allLogos,
    timeFormat,
  );

  const handleClick = ({
    workshiftId,
    startDate,
    pharmacyId,
    longTermShiftContractId,
    isAutoSyncRecord,
  }: IHandleClick) =>
    () => {
      dispatch(
        renderScreen({
          screenType: serviceWorkshiftDetails,
          screenNumber: 1,
          type: PharmacistLegendVariant.workshift,
          eventIds: [workshiftId],
          date: startDate,
          pharmacyId,
          extraParameters: {
            longTermShiftContractId,
            isAutoSyncIDMap: {
              [workshiftId]: isAutoSyncRecord,
            },
          },
        }),
      );
    };

  return (
    <>
      {events.map((item) => {
        const {
          config,
          title,
          avatar,
          workshiftId,
          pharmacyId,
          startDate,
          longTermShiftContractId,
          isAutoSyncRecord,
          contractEndDate,
          contractStartDate,
          weekDayHourlyRate,
          weekendHourlyRate,
          isInWatchlist,
          hourlyRate,
        } = item ?? {};

        const { ShiftLabel, RangeLabel } = getLongTermContractLabels({
          successAction,
          contractEndDate,
          contractStartDate,
          isLongTerm: !!longTermShiftContractId,
        });

        const rangeConfig = [
          { key: '5', value: <RangeLabel sx={styles.zeroMargin} /> },
        ];

        const label = longTermShiftContractId
          ? toWeekdayWeekendFormat(weekDayHourlyRate, weekendHourlyRate)
          : `${currency}${hourlyRate}${strings.perHour}`;

        return (
          <Box key={workshiftId} sx={styles.buttonContainer}>
            <CustomAvatarSection
              TopSection={<ShiftLabel sx={styles.shiftLabel} />}
              isAutoSyncRecord={isAutoSyncRecord}
              customMenuContainerStyle={styles.menu}
              successAction={successAction}
              config={
                longTermShiftContractId
                  ? [...rangeConfig, ...config.slice(2)]
                  : config
              }
              title={title}
              avatar={avatar}
              showFullImage={false}
              onClick={handleClick({
                workshiftId,
                startDate,
                pharmacyId,
                longTermShiftContractId: longTermShiftContractId ?? '',
                isAutoSyncRecord: !!isAutoSyncRecord,
              })}
              imageSuccessAction={successAction}
              menuActions={null}
              renderChip={
                longTermShiftContractId ? (
                  <CustomChip label={label} type={ChipType.hourlyRate} />
                ) : null
              }
              RightSection={(
                <WorkshiftListRightSection
                  isWatchlist={isInWatchlist}
                  hourlyRateLabel={label}
                />
              )}
              hourlyRate={hourlyRate}
            />
          </Box>
        );
      })}
    </>
  );
};

export default WorkshiftList;
